import React, { useMemo, useState } from "react";
import Input from "../ui/Input";

export type BreakdownColumn<T> = {
  key: string;
  label: string;
  align?: "left" | "right";
  render: (row: T) => React.ReactNode;
  sortValue?: (row: T) => number | string;
};

type Props<T> = {
  columns: BreakdownColumn<T>[];
  rows: T[];
  getRowKey: (row: T, index: number) => string;
  getSearchText: (row: T) => string;
  searchPlaceholder: string;
  emptyLabel: string;
};

const wrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "14px",
};

const searchRowStyle: React.CSSProperties = {
  maxWidth: "360px",
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: "auto",
  border: "1px solid var(--border)",
  borderRadius: "18px",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "14px 12px",
  color: "var(--text-secondary)",
  fontSize: "13px",
  fontWeight: 700,
  borderBottom: "1px solid var(--border)",
  cursor: "pointer",
  userSelect: "none",
  whiteSpace: "nowrap",
  background: "rgba(255,255,255,0.02)",
};

const tdStyle: React.CSSProperties = {
  padding: "12px 12px",
  color: "var(--text-primary)",
  fontSize: "14px",
  borderBottom: "1px solid var(--border)",
  verticalAlign: "middle",
};

const emptyStyle: React.CSSProperties = {
  padding: "20px 12px",
  color: "var(--text-secondary)",
  fontSize: "14px",
  textAlign: "center",
};

function PerformanceBreakdownTable<T>({
  columns,
  rows,
  getRowKey,
  getSearchText,
  searchPlaceholder,
  emptyLabel,
}: Props<T>) {
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => getSearchText(row).toLowerCase().includes(q));
  }, [rows, query, getSearchText]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return filteredRows;
    const column = columns.find((c) => c.key === sortKey);
    if (!column?.sortValue) return filteredRows;

    const copy = [...filteredRows];
    copy.sort((a, b) => {
      const va = column.sortValue!(a);
      const vb = column.sortValue!(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filteredRows, sortKey, sortDir, columns]);

  const handleHeaderClick = (column: BreakdownColumn<T>) => {
    if (!column.sortValue) return;
    if (sortKey !== column.key) {
      setSortKey(column.key);
      setSortDir("asc");
    } else {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    }
  };

  return (
    <div style={wrapStyle}>
      <div style={searchRowStyle}>
        <Input
          placeholder={searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div style={tableWrapStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={{ ...thStyle, textAlign: column.align || "left" }}
                  onClick={() => handleHeaderClick(column)}
                >
                  {column.label}
                  {sortKey === column.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 && (
              <tr>
                <td style={emptyStyle} colSpan={columns.length}>
                  {emptyLabel}
                </td>
              </tr>
            )}

            {sortedRows.map((row, index) => (
              <tr key={getRowKey(row, index)}>
                {columns.map((column) => (
                  <td key={column.key} style={{ ...tdStyle, textAlign: column.align || "left" }}>
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PerformanceBreakdownTable;
